/**
 * Deliver a generated PDF to the user, handling Mobile Safari / WKWebView
 * quirks.
 *
 * Why this isn't just jsPDF's `doc.save()`:
 * - `doc.save()` synthesises a hidden `<a download>` and clicks it. iOS WebKit
 *   ignores the `download` attribute entirely, so the click degrades to an
 *   in-frame navigation that races against jsPDF's blob URL revocation. Net
 *   effect on iPhone / iPad: a blank flash and no file.
 * - Inside HutBot's WKWebView (react-native-webview) WKWebView's navigation
 *   policy *also* silently rejects top-level navigation to `blob:` URLs, so
 *   even the manual `window.location.href = blob:...` fallback is a no-op.
 * - We also tried uploading the PDF to S3 and opening the presigned download
 *   URL with `Content-Disposition: attachment` via `window.open` — Mobile
 *   Safari honors that and triggers a native download, but WKWebView blocks
 *   `window.open` calls that happen after async work (activation expiry), so
 *   the new tab never opens inside HutBot. See `LEARNINGS.md` for the full
 *   trail.
 *
 * Strategies by platform:
 * - **Desktop / Android**: classic `<a download>` anchor click. Works.
 * - **iOS (Mobile Safari + HutBot WKWebView)**: Web Share API. One tap takes
 *   the user straight to "Save to Files" / AirDrop / mail. The share sheet
 *   is iOS's standard "save file" UX — every other path is either blocked or
 *   only works under specific conditions we can't guarantee inside HutBot.
 *
 * Callers decide which path to use via `isIOSDevice` from `utils/platform.ts`.
 */

/**
 * Trigger a classic download via a synthetic anchor click. Works on every
 * platform that honours `<a download>` — desktop browsers and Android Chrome.
 * iOS WebKit ignores `download`, so callers must branch via `isIOSDevice`
 * before reaching for this; use {@link deliverPdfIOS} on iOS instead.
 */
export function downloadPdf(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Give the browser a tick to start the download before we revoke.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * iOS delivery: invoke the native share sheet directly. On iOS 15.4+ this
 * works in mobile Safari AND in WKWebView (HutBot), and gives the user a
 * one-tap path to "Save to Files" — functionally indistinguishable from a
 * download.
 *
 * Must be called from a user-gesture handler (e.g. a click), otherwise iOS
 * rejects the share with `NotAllowedError`. `generateComplaintPdf` does
 * several `await fetch(...)`s before we get here, which can deplete user
 * activation on iOS < 17. If we start seeing `NotAllowedError` reports from
 * older devices, the fix is to pre-generate the PDF blob on detail-page
 * mount (move `generateComplaintPdf` into a `useEffect`, stash the result in
 * state) so this call runs synchronously off the click.
 *
 * Rejects with `AbortError` if the user dismisses the share sheet — callers
 * should swallow that case.
 *
 * Falls back to a `blob:` navigation when Web Share with files isn't
 * supported. That fallback is broken inside WKWebView but is the best we
 * can do without a host-side native bridge.
 */
export async function deliverPdfIOS(blob: Blob, filename: string): Promise<void> {
  const file = new File([blob], filename, { type: 'application/pdf' });

  if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
    await navigator.share({ files: [file], title: filename });
    return;
  }

  // Pre-iOS-15.4 / share unavailable: best-effort blob nav. In mobile Safari
  // this opens the PDF viewer; inside HutBot WKWebView it's a silent no-op,
  // but there is no better fallback that doesn't require host changes.
  const url = URL.createObjectURL(blob);
  window.location.href = url;
}
